// ============================================================
// AcceptResetScreen.tsx
// Forgot Password — step 3. The user lands here from the reset link
// in their email (App.tsx's URL-parser useEffect pulls ?token=…,
// stashes it in the store, and flips authView to 'acceptReset').
// Three render branches:
//   - tokenInvalid → "Link expired or invalid" + Request-a-new-link CTA
//   - success      → "Password updated" + auto-redirect to login (2 s)
//   - default      → the new-password / confirm-password form
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { ErrorNote } from '../shared/ActionBox'
import { acceptPasswordResetApi } from '../../api/casevoApi'
interface AcceptResetScreenProps {
  token: string
  onShowLogin: () => void
  onShowRequestReset: () => void
}
// Per-field validation errors. Mirrors RequestResetScreen / LoginScreen /
// Settings Change Password — single banner above the form is reserved for
// server-side errors (rate limits, network failures), per-field messages
// render inline below the input.
type FieldErrors = Partial<Record<'newPassword' | 'confirmPassword', string>>
const AcceptResetScreen: React.FC<AcceptResetScreenProps> = ({
  token,
  onShowLogin,
  onShowRequestReset,
}) => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  // Show/Hide toggles — one per password field, same recipe as Settings
  // Change Password (three toggles there: current, new, confirm).
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // Drives the post-submit success view. The 2-second setTimeout in
  // handleSubmit redirects to Login automatically — there's no button
  // on the success view because we want the user's hands off the keyboard
  // while the auto-redirect plays out.
  const [success, setSuccess] = useState(false)
  // Drives the token-error view. Set when the backend returns a 400 / 404
  // with a body message that smells like "invalid" or "expired" — anything
  // else stays in the form view with a banner-style error message.
  const [tokenInvalid, setTokenInvalid] = useState(false)
  // Focus the new-password field as soon as the form view mounts so the
  // user can start typing immediately. Empty deps array → runs once on
  // mount; the ref is null on the success / tokenInvalid branches but
  // ?.focus() is a no-op there so nothing to guard against.
  const newPasswordRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    newPasswordRef.current?.focus()
  }, [])
  // Runs all client-side rules and returns whichever messages applied.
  // Caller decides what to do with the result (set state vs. proceed).
  const validate = (): FieldErrors => {
    const errs: FieldErrors = {}
    if (!newPassword.trim()) {
      errs.newPassword = 'Please enter a new password'
    } else if (newPassword.length < 8) {
      errs.newPassword = 'Password must be at least 8 characters'
    } else if (newPassword.length > 100) {
      errs.newPassword = 'Password must be at most 100 characters'
    }
    if (!confirmPassword.trim()) {
      errs.confirmPassword = 'Please confirm your new password'
    } else if (confirmPassword !== newPassword) {
      errs.confirmPassword = 'Passwords do not match'
    }
    return errs
  }
  // Clears one field's error the moment the user starts editing it again.
  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Clear any stale server-error banner before re-validating, so the
    // user doesn't see a previous attempt's message sitting next to a
    // fresh per-field error.
    setError(null)
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    try {
      setLoading(true)
      setError(null)
      setFieldErrors({})
      await acceptPasswordResetApi(token, newPassword)
      setSuccess(true)
      // Auto-redirect to login after the user has had a moment to read
      // the "Password updated" message. 2 s feels right — long enough to
      // register, short enough that the user isn't left waiting.
      setTimeout(onShowLogin, 2000)
    } catch (err) {
      if (axios.isAxiosError<{ error?: unknown }>(err) && err.response) {
        const backendMessage = err.response.data?.error
        const status = err.response.status
        const messageStr = typeof backendMessage === 'string' ? backendMessage : ''
        const isTokenError =
          (status === 400 || status === 404) &&
          /invalid|expired/i.test(messageStr)
        if (isTokenError) {
          // Backend says the token is bad — flip to the "Request a new link"
          // view so the user has a single obvious next action.
          setTokenInvalid(true)
        } else if (status === 429) {
          setError('Too many reset attempts. Please try again in an hour.')
        } else if (messageStr.length > 0) {
          // Any other backend message (validation, 400 without invalid/
          // expired keywords, etc.) — surface it directly.
          setError(messageStr)
        } else {
          setError('Something went wrong. Please try again.')
        }
      } else {
        // No response received → network failure (offline, DNS, CORS,
        // timeout, server unreachable). Or a non-axios error, treated
        // the same way to be safe.
        setError('Could not connect to the server. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }
  // Base input class — border colour lives in fieldClass() below so we can
  // flip to red without fighting Tailwind's class-order resolution.
  const inputBase = `w-full border rounded-lg px-4 py-3
    text-base bg-casevo-input text-casevo-forest focus:outline-none
    focus:border-casevo-mid disabled:opacity-60 disabled:cursor-not-allowed`
  const fieldClass = (key: keyof FieldErrors) =>
    `${inputBase} ${fieldErrors[key] ? 'border-red-500' : 'border-casevo-border'}`
  // Inline spinner — `currentColor` lets it inherit the button's text colour
  // so it works on both the brand-green button and any future variants.
  const Spinner: React.FC = () => (
    <svg
      className="animate-spin h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
      />
    </svg>
  )
  // Nav bar — same recipe as LoginScreen / RequestResetScreen /
  // ConfirmationScreen. Extracted into a constant because all three
  // render branches use the same one.
  const navBar = (
    <nav className="bg-casevo-header px-4 sm:px-6 h-14 flex items-center border-b border-casevo-border">
      <div className="flex items-center gap-3">
        <svg width="28" height="28" viewBox="0 0 68 68">
          <circle cx="34" cy="34" r="34" fill="#2d6a4f"/>
          <rect x="23" y="17" width="9" height="34" rx="4.5" fill="#ffffff"/>
          <rect x="36" y="22" width="9" height="24" rx="4.5" fill="#74c69d"/>
        </svg>
        <div className="flex flex-col">
          <span className="text-white font-medium text-sm tracking-widest uppercase leading-none">Casevo</span>
          <span className="text-casevo-light text-xs tracking-widest uppercase leading-none">Case Management</span>
        </div>
      </div>
    </nav>
  )
  // ── BRANCH A: token invalid / expired ──────────────────────
  if (tokenInvalid) {
    return (
      <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
        {navBar}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Password reset</p>
              <h1 className="text-2xl font-medium text-casevo-forest">Link expired or invalid</h1>
            </div>
            <p className="text-sm text-casevo-mid mb-6">
              This reset link is invalid or has expired. Please request a new one.
            </p>
            <button
              type="button"
              onClick={onShowRequestReset}
              className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              Request a new link
            </button>
            <p className="text-center text-sm text-casevo-mid mt-6">
              <button onClick={onShowLogin} className="text-casevo-brand underline">
                Back to login
              </button>
            </p>
          </div>
        </div>
      </div>
    )
  }
  // ── BRANCH B: success / auto-redirect ──────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
        {navBar}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Password reset</p>
              <h1 className="text-2xl font-medium text-casevo-forest">Password updated</h1>
            </div>
            <p className="text-sm text-casevo-mid mb-6">
              Your password has been updated. Redirecting you to login...
            </p>
          </div>
        </div>
      </div>
    )
  }
  // ── BRANCH C: default form view ────────────────────────────
  return (
    <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
      {navBar}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm bg-casevo-surface border border-casevo-border rounded-[14px] shadow-sm p-6 sm:p-8">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">Password reset</p>
            <h1 className="text-2xl font-medium text-casevo-forest">Set a new password</h1>
          </div>
          <p className="text-sm text-casevo-mid mb-6">
            Choose a new password for your Casevo account. At least 8 characters.
          </p>
          <form onSubmit={handleSubmit}>
            {error && (
              <ErrorNote className="mb-4">{error}</ErrorNote>
            )}
            {/* New password */}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                New password
              </label>
              <div className="relative">
                <input
                  ref={newPasswordRef}
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value)
                    clearFieldError('newPassword')
                  }}
                  placeholder="Minimum 8 characters"
                  className={`${fieldClass('newPassword')} pr-12`}
                  autoComplete="new-password"
                  disabled={loading}
                  aria-invalid={!!fieldErrors.newPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-casevo-mid hover:text-casevo-forest text-xs uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {showNewPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {fieldErrors.newPassword && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.newPassword}</p>
              )}
            </div>
            {/* Confirm new password */}
            <div className="mb-6">
              <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-2">
                Confirm new password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    clearFieldError('confirmPassword')
                  }}
                  placeholder="Re-enter your new password"
                  className={`${fieldClass('confirmPassword')} pr-12`}
                  autoComplete="new-password"
                  disabled={loading}
                  aria-invalid={!!fieldErrors.confirmPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-casevo-mid hover:text-casevo-forest text-xs uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="text-xs text-casevo-danger mt-1">{fieldErrors.confirmPassword}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full bg-casevo-primary text-white text-sm py-3 rounded-lg hover:bg-casevo-primary-hover transition-colors uppercase tracking-widest disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              {loading && <Spinner />}
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
          <p className="text-center text-sm text-casevo-mid mt-6">
            <button onClick={onShowLogin} className="text-casevo-brand underline">
              Back to login
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
export default AcceptResetScreen
